import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Dialog, Spinner, Text, XStack, YStack, isWeb } from 'tamagui'
import QRCode from 'react-native-qrcode-svg'
import { Modal } from '@components/Modal'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { HeroSelect } from '@components/HeroSelect'
import { CoverImage } from '@components/CoverImage'
import { useClipboard } from '@hooks/useClipboard'
import { useRudderstack } from '@hooks/useAnalytics'
import { useThemeParams } from '@hooks/useThemeParams'
import { formatMoney } from '@utils/moneyFormatter'
import { ICustomProductListItem } from '@dtos/MembersConfig'
import { DirectPurchaseStep, useDirectPurchase } from './useDirectPurchase'
import { DirectPurchaseEntry } from './purchaseOrigin'
import { PixExpiry } from './PixExpiry'

type DirectPurchaseModalProps = {
  item: ICustomProductListItem | null
  // De onde o painel foi aberto: título, eventos do funil e origem da compra.
  entry: DirectPurchaseEntry
  onClose: () => void
  onGoToCheckout: (item: ICustomProductListItem) => void
  onPurchased: (item: ICustomProductListItem) => void
}

// Funil do painel (HET-4428). Os eventos caem no Data Lake, schema `members`, uma tabela por
// evento (ex.: `direct_purchase_panel_viewed`).
export const DIRECT_PURCHASE_EVENTS = {
  viewed: 'Direct Purchase Panel Viewed',
  submitted: 'Direct Purchase Submitted',
  pixGenerated: 'Direct Purchase Pix Generated',
  pixExpired: 'Direct Purchase Pix Expired',
  completed: 'Direct Purchase Completed',
  failed: 'Direct Purchase Failed',
  pending: 'Direct Purchase Pending',
  checkoutOpened: 'Direct Purchase Checkout Opened',
  closed: 'Direct Purchase Panel Closed',
} as const

const OUTCOME_EVENTS: Partial<Record<DirectPurchaseStep, string>> = {
  pix: DIRECT_PURCHASE_EVENTS.pixGenerated,
  pix_expired: DIRECT_PURCHASE_EVENTS.pixExpired,
  completed: DIRECT_PURCHASE_EVENTS.completed,
  failed: DIRECT_PURCHASE_EVENTS.failed,
  pending: DIRECT_PURCHASE_EVENTS.pending,
}

const textProps = {
  color: '$neutral-800',
  fontFamily: '$mulishRegular',
  fontSize: '$font-size-xxs',
} as const

// Como na Hotmart: com cartão salvo (mesmo e-mail e CPF) a compra é de 1 clique; sem ele, Pix no
// próprio painel. Qualquer outro meio fica no checkout, pelo "Trocar método de pagamento".
export function DirectPurchaseModal({
  item,
  entry,
  onClose,
  onGoToCheckout,
  onPurchased,
}: DirectPurchaseModalProps) {
  const { t } = useTranslation('DirectPurchase')
  const open = !!item
  const {
    context,
    step,
    pixCode,
    pixExpiresAt,
    purchaseMethod,
    purchase,
    retry,
  } = useDirectPurchase(item?.offerId ?? null, open)
  const { copyToClipboard } = useClipboard()
  const { track } = useRudderstack()
  const { data: themeParams } = useThemeParams()
  const [installments, setInstallments] = useState<number>()
  const [consent, setConsent] = useState(false)
  // Último passo já registrado, para cada evento sair uma vez.
  const trackedStep = useRef<DirectPurchaseStep | undefined>(undefined)

  const card = context?.cards?.[0]
  // O produtor escolhe no Xavier se o Pix aparece; a oferta precisa vender no Pix também.
  const pixAllowed =
    !!context?.offer.payment_methods.includes('pix') &&
    !!item?.paymentMethods?.includes('pix')
  const method = card ? 'credit_card' : pixAllowed ? 'pix' : null
  const options = context?.installments ?? []
  const maxOption = options.reduce<(typeof options)[number] | undefined>(
    (max, o) => (!max || o.installments > max.installments ? o : max),
    undefined,
  )
  // Parcelas começam no máximo da oferta (spec do projeto).
  const option =
    options.find((o) => o.installments === installments) ?? maxOption

  useEffect(() => {
    setConsent(false)
    setInstallments(undefined)
  }, [item])

  useEffect(() => {
    if (step === 'completed' && item) onPurchased(item)
  }, [step, item, onPurchased])

  const trackProps = useMemo(
    () => ({
      school_id: themeParams?.school?.id,
      entry,
      offer_reference: item?.offerId ?? undefined,
      school_product_id: item?.schoolProductId,
      product_name: item?.title,
    }),
    [themeParams?.school?.id, entry, item],
  )

  // O que o painel mostrou quando abriu, e o desfecho de cada compra.
  useEffect(() => {
    if (!item) {
      trackedStep.current = undefined
      return
    }
    if (step === trackedStep.current) return
    const previous = trackedStep.current
    trackedStep.current = step

    const opened = !previous || previous === 'loading'
    if (
      opened &&
      (step === 'ready' || step === 'checkout' || step === 'error')
    ) {
      const offered =
        step === 'ready' && method === 'credit_card'
          ? 'one_click'
          : step === 'ready' && method === 'pix'
            ? 'pix'
            : 'checkout'
      track(DIRECT_PURCHASE_EVENTS.viewed, {
        ...trackProps,
        offered,
        checkout_reason:
          step === 'error'
            ? 'load_error'
            : (context?.checkout_reason ??
              (offered === 'checkout' ? 'no_payment_method' : undefined)),
        has_saved_card: !!card,
        pix_allowed: pixAllowed,
      })
      return
    }

    const event = OUTCOME_EVENTS[step]
    if (event) {
      track(event, { ...trackProps, payment_method: purchaseMethod })
    }
  }, [
    step,
    item,
    method,
    card,
    pixAllowed,
    context,
    purchaseMethod,
    track,
    trackProps,
  ])

  const checkoutReason = () => {
    if (step === 'error') return 'load_error'
    if (step === 'failed') return 'payment_failed'
    if (step === 'pix_expired') return 'pix_expired'
    if (step === 'checkout')
      return context?.checkout_reason ?? 'unsupported_offer'
    if (!method) return 'no_payment_method'
    return 'change_method'
  }

  const handleClose = useCallback(() => {
    if (item && step !== 'completed') {
      track(DIRECT_PURCHASE_EVENTS.closed, { ...trackProps, step })
    }
    onClose()
  }, [item, step, track, trackProps, onClose])

  if (!open && isWeb) return null

  const installmentLabel = (o: NonNullable<typeof option>) =>
    t('installmentOption', {
      count: o.installments,
      amount: formatMoney(o.installment_amount_cents),
    })

  const handleGoToCheckout = () => {
    if (!item) return
    track(DIRECT_PURCHASE_EVENTS.checkoutOpened, {
      ...trackProps,
      reason: checkoutReason(),
    })
    onGoToCheckout(item)
  }

  const handleBuy = () => {
    if (!context) return
    if (method === 'pix') {
      track(DIRECT_PURCHASE_EVENTS.submitted, {
        ...trackProps,
        payment_method: 'pix',
      })
      return purchase({
        payment_method: 'pix',
        consent: true,
        expected_price_cents: context.offer.pix_price_cents,
        entry,
      })
    }
    track(DIRECT_PURCHASE_EVENTS.submitted, {
      ...trackProps,
      payment_method: 'credit_card',
      installments: option?.installments ?? 1,
    })
    purchase({
      payment_method: 'credit_card',
      saved_card_id: card?.id,
      installments: option?.installments ?? 1,
      consent,
      expected_price_cents: context.offer.price_cents,
      entry,
    })
  }

  const changeMethodLink = (
    <Button
      variant="secondary"
      link
      size="small"
      onPress={handleGoToCheckout}
      aria-label={t('changeMethod')}
    >
      {t('changeMethod')}
    </Button>
  )

  const renderPrice = () => {
    if (!context) return null
    if (method === 'pix') {
      return (
        <Text {...textProps} fontFamily="$mulishBold">
          {formatMoney(context.offer.pix_price_cents)}
        </Text>
      )
    }
    return (
      <YStack>
        {maxOption && maxOption.installments > 1 && (
          <Text
            {...textProps}
            fontFamily="$mulishBold"
            fontSize="$font-size-xs"
          >
            {installmentLabel(maxOption)}
          </Text>
        )}
        <Text {...textProps}>
          {t('cashPrice', { amount: formatMoney(context.offer.price_cents) })}
        </Text>
      </YStack>
    )
  }

  const renderBody = () => {
    if (step === 'loading' || step === 'processing') {
      return (
        <YStack
          alignItems="center"
          gap="$size.spacing-xs"
          py="$size.spacing-lg"
        >
          <Spinner color="$primary-300" />
          <Text {...textProps}>
            {t(step === 'loading' ? 'loading' : 'processing')}
          </Text>
        </YStack>
      )
    }

    if (step === 'pending') {
      return (
        <Text {...textProps}>
          {t(purchaseMethod === 'pix' ? 'pendingPix' : 'pending')}
        </Text>
      )
    }

    if (step === 'error' || step === 'checkout' || (context && !method)) {
      const message =
        step === 'error'
          ? t('loadError')
          : t(
              `checkoutReason.${context?.checkout_reason ?? 'unsupported_offer'}`,
            )
      return (
        <YStack gap="$size.spacing-xs">
          <Text {...textProps}>{message}</Text>
          <Button onPress={handleGoToCheckout} aria-label={t('goToCheckout')}>
            {t('goToCheckout')}
          </Button>
        </YStack>
      )
    }

    if (step === 'failed') {
      return (
        <YStack gap="$size.spacing-xs">
          <Text {...textProps} color="$danger-300" fontFamily="$mulishBold">
            {t('failed')}
          </Text>
          {/* O motivo do backend é interno (ex.: "Validation error" do antifraude) e, de propósito,
              genérico; quem precisa dele é o suporte, não o aluno. */}
          <Text {...textProps}>{t('failedHint')}</Text>
          <Button onPress={retry} aria-label={t('tryAgain')}>
            {t('tryAgain')}
          </Button>
          {changeMethodLink}
        </YStack>
      )
    }

    if (step === 'pix_expired') {
      return (
        <YStack gap="$size.spacing-xs">
          <Text {...textProps}>{t('pixExpired')}</Text>
          <Button variant="cta" onPress={handleBuy} aria-label={t('newPix')}>
            {t('newPix')}
          </Button>
          {changeMethodLink}
        </YStack>
      )
    }

    if (step === 'pix' && pixCode) {
      return (
        <YStack alignItems="center" gap="$size.spacing-xs">
          <Text {...textProps} textAlign="center">
            {t('pixInstructions')}
          </Text>
          <QRCode value={pixCode} size={180} />
          <Button
            variant="secondary"
            onPress={() => copyToClipboard(pixCode)}
            aria-label={t('copyPix')}
          >
            {t('copyPix')}
          </Button>
          {pixExpiresAt && <PixExpiry expiresAt={pixExpiresAt} />}
          <XStack gap="$size.spacing-xxxs" alignItems="center">
            <Spinner size="small" color="$primary-300" />
            <Text {...textProps}>{t('waitingPix')}</Text>
          </XStack>
        </YStack>
      )
    }

    if (!context) return null

    if (method === 'pix') {
      const pixLabel = t('generatePix', {
        amount: formatMoney(context.offer.pix_price_cents),
      })
      return (
        <YStack gap="$size.spacing-xs">
          <Button variant="cta" onPress={handleBuy} aria-label={pixLabel}>
            {pixLabel}
          </Button>
          {changeMethodLink}
        </YStack>
      )
    }

    if (!card || !option) return null

    return (
      <YStack gap="$size.spacing-xs">
        <HeroSelect
          label={t('installments')}
          options={options.map((o) => ({ name: installmentLabel(o) }))}
          value={installmentLabel(option).toLowerCase()}
          onValueChange={(value) => {
            const chosen = options.find(
              (o) => installmentLabel(o).toLowerCase() === value.toLowerCase(),
            )
            if (chosen) setInstallments(chosen.installments)
            setConsent(false)
          }}
        />
        <XStack alignItems="center" gap="$size.spacing-xxxs">
          <HeroIcon
            icon={['fal', 'credit-card']}
            size={20}
            color="$neutral-800"
          />
          <Text {...textProps}>
            {t('useCard', {
              brand: card.brand?.toUpperCase(),
              lastDigits: card.last_digits,
            })}
          </Text>
        </XStack>
        <XStack alignItems="flex-start" gap="$size.spacing-xxxs">
          <Checkbox
            id="direct-purchase-consent"
            checked={consent}
            onCheckedChange={(checked) => setConsent(checked === true)}
            aria-label={t('consentLabel')}
            // O size padrão do Checkbox ($true) vale 4px neste tema e achata a
            // caixa; o fundo padrão é o do <button> do navegador.
            width="$size.spacing-sm"
            height="$size.spacing-sm"
            borderWidth="$border-width-hairline"
            borderColor={consent ? '$primary-300' : '$neutral-500'}
            borderRadius="$border-radius-sm"
            backgroundColor={consent ? '$primary-300' : 'transparent'}
          >
            <Checkbox.Indicator>
              <HeroIcon icon={['fal', 'check']} size={14} color="$neutral-0" />
            </Checkbox.Indicator>
          </Checkbox>
          <Text {...textProps} flex={1} onPress={() => setConsent(!consent)}>
            {t('consent', {
              amount: installmentLabel(option),
              lastDigits: card.last_digits,
            })}
          </Text>
        </XStack>
        <Button
          variant="cta"
          disabled={!consent}
          onPress={handleBuy}
          aria-label={t('buyOneClick')}
        >
          {t('buyOneClick')}
        </Button>
        {changeMethodLink}
      </YStack>
    )
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      snapPointsMode="fit"
      backdropOpacity={0.8}
      maxWidth={440}
    >
      <YStack
        gap="$size.spacing-xs"
        paddingHorizontal="$size.spacing-md"
        paddingVertical="$size.spacing-lg"
      >
        <Dialog.Title
          color="$neutral-800"
          fontFamily="$mulishBold"
          fontSize="$font-size-sm"
          pr="$size.spacing-lg"
        >
          {t(`panelTitle.${entry}`)}
        </Dialog.Title>
        {item && (
          <XStack gap="$size.spacing-xxs" alignItems="center">
            <CoverImage
              uri={item.logo}
              width={96}
              height={54}
              borderRadius={4}
            />
            <YStack flex={1}>
              <Text {...textProps} fontFamily="$mulishBold">
                {item.title}
              </Text>
              {renderPrice()}
            </YStack>
          </XStack>
        )}
        {renderBody()}
      </YStack>
    </Modal>
  )
}
